/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect} from 'react';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import Polyline from '@arcgis/core/geometry/Polyline';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import {loadModules} from 'esri-loader';
import {error} from '@ion/core/src/logger/logger';
import {useKeyPress} from '../../hooks/useKeyPress';
import {dlsColor} from '../../theme/theme';
import {
  getExponentialBuffer,
  getMetersInDegree
} from '../helpers/globeUtils';
import {
  getMapPoints,
  removeActiveFence
} from '../helpers/MapViewUtils';
import {DEFAULT_SYMBOL, DEFAULT_UNIT} from './constants';

// defines the prop definitions for the CreateRadial
export type CreateRadialProps = {
  map: __esri.Map,
  mapView: __esri.MapView,
  radius?: number;
  radiusFromPanel?: number;
  setRadiusFromMap?: Function;
  setDrawingComplete?: (val: boolean) => void;
  setFinalDrawingDetails?: (val: {rings: Array<any>}) => void;
  finishedDrawing?: boolean;
  isFMTracking?: boolean;
  mapViewEl?: HTMLElement;
  geoFencesGraphicsLayerRef?: {
    current: null | __esri.Layer;
  };
  sketchViewModelRef?: {
    current: null | __esri.SketchViewModel;
  };
  selectedMapPointRef?: {
    current: null | __esri.Layer;
  };
  cancelDrawing?: Function;
  mapClickHandler?: any;
  setMapClickHandler?: (val: any) => void;
};

type RadialProps = {
  GraphicsLayer: __esri.GraphicsLayer;
  Graphic: __esri.Graphic;
  SketchViewModel: __esri.SketchViewModel;
  Polyline: __esri.Polyline;
  geometryEngine: __esri.geometryEngine;
};

/**
 * Provides Drawing radial functionality
 *
 * ```typescript
 *
 *    const [radiusValue, setRadiusValue] = useState(100);
 *
 *    <CreateRadial map={map} mapView={mapView} radius={radiusValue} />
 *
 * ```
 *
 */
export const CreateRadial = ({
  map,
  mapView,
  radius: radiusValue,
  radiusFromPanel,
  setRadiusFromMap,
  setDrawingComplete,
  setFinalDrawingDetails,
  finishedDrawing,
  mapViewEl,
  geoFencesGraphicsLayerRef,
  sketchViewModelRef,
  selectedMapPointRef,
  cancelDrawing,
  mapClickHandler,
  setMapClickHandler,
  isFMTracking = false
}: CreateRadialProps) => {
  const deleteKey    = useKeyPress('Delete');
  const escapeKey    = useKeyPress('Escape');

  useEffect(() => {
    initializeDrawing();
  }, []);

  useEffect(() => {
    if (radiusFromPanel) {
      initializeDrawing();
    }
    return () => {
      mapViewEl?.classList?.remove('default-cursor');
      mapViewEl?.classList?.remove('crosshair');
    };
  }, [radiusValue]);

  useEffect(() => {
    return function() {
      if (geoFencesGraphicsLayerRef.current) {
        sketchViewModelRef.current?.complete();
        sketchViewModelRef.current?.cancel();
        map.remove(geoFencesGraphicsLayerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (deleteKey || escapeKey) {
      if (geoFencesGraphicsLayerRef.current) {
        // If the user is currently manipulating a drawing
        // graphic delete it and re-initialize the drawing tool
        removeActiveFence(
          map,
          [geoFencesGraphicsLayerRef, sketchViewModelRef, selectedMapPointRef]
        );
        cancelDrawing();
        setDrawingComplete(false);
        mapClickHandler?.remove();
      }
    }
  }, [deleteKey, escapeKey]);

  useEffect(() => {
    if (finishedDrawing) {
      const items = geoFencesGraphicsLayerRef.current?.graphics?.items;
      if (items?.length && items[0].geometry?.rings) {
        setFinalDrawingDetails(items[0].geometry.rings[0]);
      }
    };
  }, [finishedDrawing]);

  useEffect(() => {
    if (selectedMapPointRef?.current) {
      initializeDrawing();
      setDrawingComplete(true);
    }
  }, [selectedMapPointRef?.current]);

  function initializeDrawing() {
    if (!isFMTracking) {
      loadModules([
        'esri/layers/GraphicsLayer',
        'esri/Graphic',
        'esri/widgets/Sketch/SketchViewModel',
        'esri/geometry/Polyline',
        'esri/geometry/geometryEngine'
      ])
        .then(
          ([GraphicsLayer, Graphic, SketchViewModel, Polyline, geometryEngine]) => {
            createRadial({GraphicsLayer, Graphic, SketchViewModel, Polyline, geometryEngine});
          }
        )
        .catch((err) => {
          error(err);
        });
    } else {
      createRadial({GraphicsLayer, Graphic, SketchViewModel, Polyline, geometryEngine} as any);
    }
  };

  const createRadial = useCallback(({
    Graphic,
    Polyline,
    GraphicsLayer,
    geometryEngine,
    SketchViewModel
  }: RadialProps) => {
    let centerGraphic,
      edgeGraphic,
      polylineGraphic,
      bufferGraphic,
      centerGeometryAtStart;
    if (selectedMapPointRef?.current) {
      drawBufferPolygon(
        selectedMapPointRef.current.longitude,
        selectedMapPointRef.current.latitude,
        true
      );
    } else {
      const viewClickHandler = mapView.on('immediate-click', function(event) {
        event.stopPropagation();
        if (sketchViewModelRef?.current?.state === 'active') {
          return;
        }
        selectedMapPointRef.current = event.mapPoint;
        drawBufferPolygon(event.mapPoint.longitude, event.mapPoint.latitude, false);
        setDrawingComplete(true);
      });
      setMapClickHandler(viewClickHandler);
      mapViewEl?.classList?.add('crosshair');
      mapViewEl?.classList?.remove('default-cursor');
    }

    function drawBufferPolygon(mapPointLongitude, mapPointLatitude, editing) {
      const pi      = Math.PI;
      const degrees = getMetersInDegree(1); // make radial buffer 1 meter

      // this means that the user is dragging the radius so we dont need to
      // recalculate the radius it's already being called in onMove
      if (editing && !radiusFromPanel) return;

      // if editing is false then the user just placed the initial circle on the map
      // use a calculated radius based on the zoom level and set the radius value
      // as such
      const newLatitude  =
      mapPointLatitude +
      (editing ? radiusValue : getExponentialBuffer(mapView.zoom)) * degrees;
      const newLongitude =
      mapPointLongitude +
      ((editing ? radiusValue : getExponentialBuffer(mapView.zoom)) * degrees) /
      Math.cos(mapPointLatitude * (pi / 180));
      if (!editing) {
        setRadiusFromMap(Math.round(getExponentialBuffer(mapView.zoom)));
      };
      const {
        vertices,
        centerPoint,
        edgePoint
      } = getMapPoints({
        mapView,
        mapPointLongitude,
        mapPointLatitude,
        newLatitude,
        newLongitude
      });

      // Create center, edge, polyline and buffer graphics
      const polyline = new Polyline({
        paths            : vertices,
        spatialReference : mapView.spatialReference
      });

      // get the length of the polyline and create buffer
      const length = geometryEngine.geodesicLength(polyline, DEFAULT_UNIT);
      const buffer = geometryEngine.geodesicBuffer(
        centerPoint,
        length,
        DEFAULT_UNIT
      );

      // Create the graphics representing the line and buffer
      const pointSymbol = {
        type  : 'simple-marker',
        style : 'circle',
        size  : 10,
        color : dlsColor('skyBlue')
      };

      centerGraphic = new Graphic({
        geometry   : centerPoint,
        symbol     : pointSymbol,
        attributes : {
          center : 'center'
        }
      });

      edgeGraphic = new Graphic({
        geometry : edgePoint,
        symbol   : {
          type  : 'simple-marker',
          style : 'circle',
          size  : 10,
          color : '#FFFFFF'
        },
        attributes : {
          edge : 'edge'
        }
      });

      polylineGraphic = new Graphic({
        geometry : polyline,
        symbol   : {
          type  : 'simple-line',
          color : dlsColor('skyBlue'),
          width : 3
        }
      });

      bufferGraphic = new Graphic({
        geometry : buffer,
        symbol   : DEFAULT_SYMBOL()
      });

      if (geoFencesGraphicsLayerRef.current) {
        sketchViewModelRef.current?.cancel();
        (geoFencesGraphicsLayerRef.current as any).removeAll();
      } else {
        geoFencesGraphicsLayerRef.current = new GraphicsLayer();
        map.layers.add(geoFencesGraphicsLayerRef.current);
        map.layers.reorder(geoFencesGraphicsLayerRef.current, 1);
      }
      // Add graphics to layer
      geoFencesGraphicsLayerRef.current?.addMany([
        bufferGraphic,
        centerGraphic,
        edgeGraphic,
        polylineGraphic
      ]);

      setUpSketch();

      sketchViewModelRef.current?.update([edgeGraphic, centerGraphic], {
        tool : 'move'
      });

      calculateBuffer(vertices);
    }

    /*
     * Edge or center point is being updated. Recalculate the buffer with
     * updated geometry information.
     */
    function calculateBuffer(vertices) {
      polylineGraphic.geometry = new Polyline({
        paths            : vertices,
        spatialReference : mapView.spatialReference
      });

      // Recalculate the polyline length and buffer polygon
      const length = geometryEngine.geodesicLength(
        polylineGraphic.geometry,
        DEFAULT_UNIT
      );

      const buffer = geometryEngine.geodesicBuffer(
        centerGraphic.geometry,
        length,
        DEFAULT_UNIT
      );

      // Update the buffer polygon
      bufferGraphic.geometry = buffer;
    }

    function setUpSketch() {
      sketchViewModelRef.current = new SketchViewModel({
        view                 : mapView,
        layer                : geoFencesGraphicsLayerRef.current,
        updateOnGraphicClick : false,
        defaultUpdateOptions : {
          toggleToolOnClick : false
        }
      });
      mapViewEl?.classList.add('default-cursor');
      mapViewEl?.classList.remove('crosshair');

      // Listen to SketchViewModel's update event
      sketchViewModelRef.current?.on('update', onMove);
    }

    /*
     * Edge or center graphics are being moved. Recalculate the buffer with
     * updated geometry information again.
     */
    function onMove(event) {
    // If the edge graphic is moving, keep the center graphic
    // at its initial location. Only move edge graphic
      if (event.toolEventInfo?.mover?.attributes?.edge) {
        const toolType = event.toolEventInfo.type;
        if (toolType === 'move-start') {
          centerGeometryAtStart = centerGraphic.geometry;
        } else if (toolType === 'move' || toolType === 'move-stop') {
          // keep the center graphic at its initial location when edge point is moving
          centerGraphic.geometry = centerGeometryAtStart;
        }
      }

      // the center or edge graphic is being moved, recalculate the buffer
      const vertices = [
        [centerGraphic.geometry.x, centerGraphic.geometry.y],
        [edgeGraphic.geometry.x, edgeGraphic.geometry.y]
      ];
      calculateBuffer(vertices);

      if (event.state === 'complete') {
        sketchViewModelRef.current?.update([edgeGraphic, centerGraphic], {
          tool : 'move'
        });
      } else if (event.state === 'active') {
      // calculate the radius length
        const length = geometryEngine.geodesicLength(
          polylineGraphic.geometry,
          DEFAULT_UNIT
        );
        setRadiusFromMap(Math.round(length));
      }
    }
  }, [selectedMapPointRef?.current, radiusValue, radiusFromPanel]);

  return null;
};

export default CreateRadial;
